'use client';

import {
  createContext,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  type HTMLAttributes,
  type MouseEvent,
  type ReactNode,
} from 'react';
import { cn } from '../../lib/cn';
import { SIcon } from '../SIcon';

export interface SExpansionItemRenderState {
  expanded: boolean;
  selected: boolean;
  disabled: boolean;
}

export type SExpansionItemRenderProp = (state: SExpansionItemRenderState) => ReactNode;
export type SExpansionItemInteraction = 'chevron';
export type SExpansionItemSupportingTextPosition = 'right' | 'bottom';
export type SExpansionItemSize = 'sm' | 'md';

export interface SExpansionItemProps extends Omit<
  HTMLAttributes<HTMLDivElement>,
  'onToggle' | 'title'
> {
  title: ReactNode;
  /** 제목을 보조하는 텍스트 */
  supportingText?: ReactNode;
  /** 보조 텍스트 위치 */
  supportingTextPosition?: SExpansionItemSupportingTextPosition;
  /** 중첩 단계. Figma 기준 1~3단계 */
  depth?: 1 | 2 | 3;
  /** 타이틀 앞에 표시할 아이콘/콘텐츠 */
  leading?: ReactNode | SExpansionItemRenderProp;
  /** 타이틀 뒤에 표시할 태그/콘텐츠 */
  trailing?: ReactNode;
  /** body 내부 패딩 사용 여부 */
  bodyPadding?: boolean;
  /** 외곽 테두리 사용 여부 */
  bordered?: boolean;
  expanded?: boolean;
  defaultExpanded?: boolean;
  selected?: boolean;
  /** hover/selected 상태에서 적용할 인터랙션 preset */
  interaction?: SExpansionItemInteraction;
  /** 아이템 왼쪽 accent stripe 표시 여부 */
  accentStripe?: boolean;
  dense?: boolean;
  /** 타이포그래피 크기 */
  size?: SExpansionItemSize;
  disabled?: boolean;
  onToggle?: (expanded: boolean, event: MouseEvent<HTMLButtonElement>) => void;
}

interface SExpansionItemDescendantContextValue {
  reportExpandedDescendant: (delta: number) => void;
}

const SExpansionItemDescendantContext = createContext<SExpansionItemDescendantContextValue | null>(
  null,
);

export const SExpansionItem = forwardRef<HTMLDivElement, SExpansionItemProps>(
  function SExpansionItem(
    {
      title,
      supportingText,
      supportingTextPosition = 'right',
      depth = 1,
      leading,
      trailing,
      bodyPadding = true,
      bordered = false,
      expanded,
      defaultExpanded = false,
      selected = false,
      interaction,
      accentStripe = false,
      dense = false,
      size = 'sm',
      disabled = false,
      onToggle,
      className,
      children,
      ...rest
    },
    ref,
  ) {
    const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
    const [expandedDescendantCount, setExpandedDescendantCount] = useState(0);
    const [hovered, setHovered] = useState(false);
    const parentDescendantContext = useContext(SExpansionItemDescendantContext);
    const isExpanded = expanded ?? internalExpanded;
    const isSelected = isExpanded && (selected || expandedDescendantCount === 0);
    const hasChevronInteraction = interaction === 'chevron';
    const showInteractionChevron = hasChevronInteraction && !disabled && (isSelected || hovered);
    const interactionChevronColor = isSelected
      ? 'var(--sys-color-fg-brand)'
      : 'var(--sys-color-fg-quaternary)';
    const titleTypo =
      size === 'sm'
        ? isExpanded
          ? 'typo-item-sm-selected'
          : 'typo-item-sm-default'
        : isExpanded
          ? 'typo-item-md-selected'
          : 'typo-item-md-default';
    const reportExpandedDescendant = useCallback(
      (delta: number) => {
        setExpandedDescendantCount(current => Math.max(0, current + delta));
        parentDescendantContext?.reportExpandedDescendant(delta);
      },
      [parentDescendantContext],
    );
    const descendantContextValue = useMemo<SExpansionItemDescendantContextValue>(
      () => ({ reportExpandedDescendant }),
      [reportExpandedDescendant],
    );
    const leadingNode =
      typeof leading === 'function'
        ? leading({ expanded: isExpanded, selected: isSelected, disabled })
        : leading;
    const hasSupportingText = supportingText != null && supportingText !== '';
    const id = useId();

    useEffect(() => {
      if (!isExpanded) return;

      parentDescendantContext?.reportExpandedDescendant(1);
      return () => parentDescendantContext?.reportExpandedDescendant(-1);
    }, [isExpanded, parentDescendantContext]);

    return (
      <div
        ref={ref}
        className={cn(
          'group/expansion-item relative overflow-hidden bg-(--cmp-listExpansionItem-title-bg-default)',
          !bordered && 'border-b border-(--cmp-listExpansionItem-border-default)',
          // 보더 색은 시맨틱 토큰(border-default → hover strong → 펼침 accentLight)을 쓴다.
          // TODO: expansionItem/bordered/radius 토큰 생성 후 rounded 고정값도 토큰으로 교체한다.
          bordered &&
            'rounded-[8px] border border-border-default hover:border-border-strong [&:has(>button:hover)]:border-border-strong',
          bordered && isExpanded && '!border-border-accentLight',
          className,
        )}
        {...rest}
      >
        {accentStripe && selected && !bordered && (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute bottom-[-1px] left-0 top-0 z-[1] w-(--cmp-listItem-accentStripe-width) bg-(--cmp-listItem-accentStripe-color)"
          />
        )}
        <button
          type="button"
          disabled={disabled}
          aria-expanded={isExpanded}
          aria-controls={id}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={event => {
            if (disabled) return;
            const next = !isExpanded;
            if (expanded === undefined) setInternalExpanded(next);
            onToggle?.(next, event);
          }}
          className={cn(
            'group/expansion-item-button flex w-full cursor-pointer items-center justify-between border-0 text-left text-(--cmp-listExpansionItem-title-text-default) focus-visible:outline focus-visible:outline-2 focus-visible:outline-(--sys-color-border-accent) disabled:cursor-not-allowed disabled:text-(--sys-color-disabled-text)',
            titleTypo,
            isExpanded && 'border-b border-(--cmp-listExpansionItem-title-border)',
            hasChevronInteraction
              ? 'hover:bg-(--sys-color-bg-neutralBright)'
              : !isExpanded && 'hover:bg-(--cmp-listExpansionItem-title-bg-hover)',
            hasSupportingText && supportingTextPosition === 'bottom'
              ? dense
                ? 'min-h-[52px] py-(--cmp-listExpansionItem-title-paddingY-dense)'
                : 'min-h-[60px] py-(--cmp-listExpansionItem-title-paddingY-normal)'
              : dense
                ? 'h-[32px] py-(--cmp-listExpansionItem-title-paddingY-dense)'
                : 'h-[40px] py-(--cmp-listExpansionItem-title-paddingY-normal)',
            'gap-(--cmp-listExpansionItem-title-gap)',
            'pr-(--cmp-listExpansionItem-title-paddingX)',
            depth === 2 ? 'pl-[36px]' : depth === 3 ? 'pl-[56px]' : 'pl-[16px]',
            isExpanded &&
              cn(
                'text-(--cmp-listExpansionItem-title-text-expanded)',
                isSelected && 'bg-(--cmp-listExpansionItem-title-bg-expanded)',
              ),
          )}
        >
          {!leading && (
            <span
              className={cn(
                'flex transition-transform duration-200 ease-out motion-reduce:transition-none',
                isExpanded && 'rotate-180',
              )}
            >
              <SIcon name="caretDown" size={12} color="var(--sys-color-fg-quaternary)" />
            </span>
          )}
          {leadingNode}
          <span
            className={cn(
              'min-w-0 flex-1',
              supportingTextPosition === 'bottom'
                ? 'flex flex-col'
                : 'flex items-center gap-[8px] whitespace-nowrap',
            )}
          >
            <span className="min-w-0 truncate">{title}</span>
            {hasSupportingText && (
              <span
                className={cn(
                  'typo-body-sm-default min-w-0 truncate text-(--sys-color-fg-secondary)',
                  supportingTextPosition === 'right' && 'flex-1',
                )}
              >
                {supportingText}
              </span>
            )}
          </span>
          {trailing}
          {showInteractionChevron && (
            <SIcon name="chevronRight" size={12} color={interactionChevronColor} />
          )}
        </button>
        <SExpansionItemDescendantContext.Provider value={descendantContextValue}>
          <div
            id={id}
            aria-hidden={!isExpanded}
            className={cn(
              'grid overflow-hidden bg-(--cmp-listExpansionItem-body-bg) transition-[grid-template-rows] duration-200 ease-out motion-reduce:transition-none',
              isExpanded ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]',
            )}
          >
            <div className="min-h-0 overflow-hidden">
              <div className={cn(bodyPadding && 'p-(--cmp-listExpansionItem-body-padding)')}>
                {children}
              </div>
            </div>
          </div>
        </SExpansionItemDescendantContext.Provider>
      </div>
    );
  },
);
